import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { getBaseReleaseVersion, getCurrentOrgId, getCurrentOrgName, getSessionInfo, isOrgUiEnabled, setSessionInfo, } from '@thoughtspot/session-service';
import React, { useEffect, useRef, useState } from 'react';
import { getConfigInfoOverrides, updateConfigInfo, } from "../../../services/system/system-service";
import { AutoTranslationToggle } from './auto-translation-toggle';
export const AutoTranslationToggleContainer = ({ orgViewModeType, onEditingChange, onLoadingChange, onSavingChange, onShowToast, }) => {
    const isOrgEnabled = isOrgUiEnabled();
    const isAllOrgContext = !isOrgEnabled || orgViewModeType === 'all-org';
    const isMountedRef = useRef(true);
    const [enabled, setEnabled] = useState(false);
    const [clusterEnabled, setClusterEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [pendingValue, setPendingValue] = useState(false);
    const { t } = useTranslation();
    const canModify = isAllOrgContext || clusterEnabled;
    const perOrgMapRef = useRef({});
    const releaseVersion = getBaseReleaseVersion();
    const LEARN_MORE_URL = `https://docs.thoughtspot.com/cloud/${releaseVersion}/auto-translation`;
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const fetchConfig = async () => {
        try {
            setLoading(true);
            const config = await getConfigInfoOverrides();
            if (isAllOrgContext) {
                const clusterValue = Boolean(config.automaticTranslationOfObjectMetadata);
                if (!isMountedRef.current) {
                    return;
                }
                setEnabled(clusterValue);
                setClusterEnabled(clusterValue);
                setPendingValue(clusterValue);
                perOrgMapRef.current =
                    config.automaticTranslationOfObjectMetadataPerOrg || {};
            }
            else {
                const orgId = getCurrentOrgId().toString();
                const backendPerOrg = config.automaticTranslationOfObjectMetadataPerOrg || {};
                perOrgMapRef.current = {
                    ...perOrgMapRef.current,
                    ...backendPerOrg,
                };
                const clusterValue = Boolean(config.automaticTranslationOfObjectMetadata);
                const overrideValue = perOrgMapRef.current[orgId] !== undefined
                    ? Boolean(perOrgMapRef.current[orgId])
                    : undefined;
                let effectiveValue;
                if (clusterValue === false) {
                    effectiveValue = false;
                }
                else if (overrideValue !== undefined) {
                    effectiveValue = overrideValue;
                }
                else {
                    effectiveValue = clusterValue;
                }
                if (!isMountedRef.current) {
                    return;
                }
                setClusterEnabled(clusterValue);
                setEnabled(effectiveValue);
                setPendingValue(effectiveValue);
            }
        }
        catch (error) {
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };
    useEffect(() => {
        fetchConfig();
    }, [orgViewModeType]);
    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);
    useEffect(() => {
        onSavingChange?.(saving);
    }, [saving, onSavingChange]);
    const handleSave = async (newValue) => {
        try {
            setSaving(true);
            let changes;
            let updatedClusterValue;
            let updatedPerOrgConfig;
            if (isAllOrgContext) {
                updatedClusterValue = newValue;
                updatedPerOrgConfig = newValue ? {} : undefined;
                changes = newValue
                    ? {
                        automaticTranslationOfObjectMetadata: newValue,
                        automaticTranslationOfObjectMetadataPerOrg: {},
                    }
                    : {
                        automaticTranslationOfObjectMetadata: newValue,
                    };
            }
            else {
                const orgId = getCurrentOrgId().toString();
                const updatedOrgConfig = {
                    ...perOrgMapRef.current,
                    [orgId]: newValue,
                };
                perOrgMapRef.current = updatedOrgConfig;
                updatedPerOrgConfig = updatedOrgConfig;
                changes = {
                    automaticTranslationOfObjectMetadataPerOrg: {
                        [orgId]: newValue,
                    },
                };
            }
            await updateConfigInfo(changes);
            if (!isMountedRef.current) {
                return;
            }
            setEnabled(newValue);
            setPendingValue(newValue);
            if (isAllOrgContext) {
                setClusterEnabled(newValue);
            }
            const existingSessionInfo = getSessionInfo();
            if (existingSessionInfo) {
                const newConfigInfo = {
                    ...existingSessionInfo.configInfo,
                    ...(updatedClusterValue !== undefined && {
                        automaticTranslationOfObjectMetadata: updatedClusterValue,
                    }),
                    ...(updatedPerOrgConfig && {
                        automaticTranslationOfObjectMetadataPerOrg: updatedPerOrgConfig,
                    }),
                };
                setSessionInfo({
                    ...existingSessionInfo,
                    configInfo: newConfigInfo,
                });
            }
            setIsEditing(false);
            onEditingChange?.(false);
            onShowToast?.(t('tscli.ui.appsettings.datasave.success.message'));
            collectEvent(new UserAction(userActionTypeEnum.AUTO_TRANSLATE), {
                newStatus: newValue ? 'enabled' : 'disabled',
                oldStatus: enabled ? 'enabled' : 'disabled',
                org: isAllOrgContext ? 'All Orgs' : getCurrentOrgName(),
            });
        }
        catch (error) {
        }
        finally {
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    };
    const startEditing = () => {
        if (!canModify || saving) {
            return;
        }
        setPendingValue(enabled);
        setIsEditing(true);
        onEditingChange?.(true);
    };
    const cancelEditing = () => {
        setPendingValue(enabled);
        setIsEditing(false);
        onEditingChange?.(false);
    };
    const handleToggleChange = (value) => {
        setPendingValue(value);
    };
    const handleSaveClick = () => {
        handleSave(pendingValue);
    };
    if (loading) {
        return null;
    }
    return (React.createElement(AutoTranslationToggle, { enabled: enabled, pendingValue: pendingValue, onToggleChange: handleToggleChange, onSave: handleSaveClick, onEdit: startEditing, onCancel: cancelEditing, isEditing: isEditing, isClusterContext: isAllOrgContext, clusterEnabled: clusterEnabled, learnMoreUrl: LEARN_MORE_URL, saving: saving, canModify: canModify }));
};
//# sourceMappingURL=auto-translation-toggle.container.js.map